import React, {createRef} from 'react';
import {ColumnSpec, TreeStyle} from '@xh/hoist/cmp/grid';
import {strong} from '@xh/hoist/cmp/layout';
import {HoistModel, managed, PlainObject, XH} from '@xh/hoist/core';
import {FilterLike, View} from '@xh/hoist/data';
import {action, observable} from '@xh/hoist/mobx';
import {ConfigurableGridModel} from '../../../../cmp/gridSpec/ConfigurableGridModel';
import {buildFilter, lockSingleLeafGroups} from '../../../../cmp/gridSpec/GridSpecBuilder';
import {DEFAULT_GRID_SPEC, GridSpec} from '../../../../cmp/gridSpec/GridSpecTypes';
import {isTickerRow, tickerChangeClassRules, tickerCol} from '../../../../cmp/PositionColumns';
import {Position} from '../../../../data/DataTypes';
import {
    migratePositionsState,
    positionsBaseColumn,
    positionsCapabilities,
    positionsCatalog,
    positionsStoreFields
} from '../../../performance/widgets/positions/PositionsGridSpec';
import {OrderTicketModel} from '../orders/OrderTicketModel';
import {TransactionsTabModel} from '../transactions/TransactionsTabModel';
import {PositionImporterModel} from './PositionImporterModel';
import {tickerPopup} from './TickerPanel';
import {TickerPanelModel} from './TickerPanelModel';

/** Pref holding the grid's GridSpec - and, before GridSpec, the persisted GridModel state at `grid`. */
const PREF_KEY = 'positionsTabState';

const portfolioPositionsDefaultSpec: GridSpec = {
    ...DEFAULT_GRID_SPEC,
    columns: [
        {field: 'ticker', pinned: 'left'},
        {field: 'price'},
        {field: 'priceChange'},
        {field: 'priceChangePct'},
        {field: 'intraday'},
        {field: 'yearRange'},
        {field: 'marketValue', aggregator: 'sum'},
        {field: 'quantity'},
        {field: 'avgPrice'},
        {field: 'totalCost', aggregator: 'sum'},
        {field: 'pnlDly', aggregator: 'sum'},
        {field: 'pnlYtd', aggregator: 'sum'},
        {field: 'pnlOpen', aggregator: 'sum'},
        {field: 'beta'},
        {field: 'lastDividend'},
        {field: 'sector'}
    ]
};

export class PositionsModel extends HoistModel {
    @observable accessor portfolioId: number;
    @managed configurableGridModel = this.createConfigurableGridModel();
    @managed tickerModel = new TickerPanelModel();
    @managed view = this.createView();
    filePickerRef = React.createRef<HTMLInputElement>();
    @managed importer = new PositionImporterModel();
    @managed orderTicketModel = new OrderTicketModel();
    @managed transactionsModel = new TransactionsTabModel();
    panelRef = createRef<HTMLElement>();
    visible = false;

    constructor() {
        super();

        this.addReaction({
            track: () => [this.portfolioId, this.spec.groupBy, this.spec.filters, this.spec.showLeafRows],
            run: () => this.view.updateQuery(this.viewQuery)
        });
    }

    get gridModel() {
        return this.configurableGridModel.gridModel;
    }

    get spec(): GridSpec {
        return this.configurableGridModel.specModel.spec;
    }

    @action
    async setPortfolio(portfolioId: number) {
        this.portfolioId = portfolioId;
    }

    get selectedRecord() {
        return this.gridModel.selectedRecord;
    }

    get gridFilter() {
        return this.gridModel.filterModel.filter;
    }

    clearFilters() {
        this.gridModel.filterModel.clear();
        this.gridModel.filterModel.setFilter(null);
    }

    openFile() {
        this.filePickerRef.current.click();
    }

    import(file: File) {
        this.importer.open(file);
        this.filePickerRef.current.value = '';
    }

    newOrder() {
        this.orderTicketModel.open(this.portfolioId, null, 'BUY', 0);
    }

    openConfigurator() {
        this.configurableGridModel.openConfigurator();
    }

    /** The selected position - null for a group row. */
    get selectedPosition(): Position {
        const raw = this.gridModel.selectedRecord?.raw;
        return raw && raw.cubeDimension == null ? (raw as Position) : null;
    }

    showTransactions() {
        const {portfolioId, securityId} = this.selectedPosition;
        this.transactionsModel.open({portfolioId, securityId});
    }

    private get viewQuery() {
        const {spec} = this;
        return {
            // Without grouping there are only leaves to show
            includeLeaves: spec.showLeafRows || !spec.groupBy.length,
            lockFn: lockSingleLeafGroups,
            dimensions: spec.groupBy,
            filter: [{field: 'portfolioId', op: '=', value: this.portfolioId ?? -1}, ...buildFilter(spec)] as FilterLike
        };
    }

    private createView(): View {
        return XH.livePositionsService.cube.createView({
            query: this.viewQuery,
            stores: this.gridModel.store,
            connect: true
        });
    }

    private createConfigurableGridModel(): ConfigurableGridModel {
        const state: PlainObject = XH.getPref(PREF_KEY, {}) ?? {};
        return new ConfigurableGridModel({
            title: 'Positions',
            catalog: positionsCatalog,
            capabilities: positionsCapabilities,
            defaultSpec: portfolioPositionsDefaultSpec,
            initialSpec: state.gridSpec ?? migratePositionsState(state.grid, portfolioPositionsDefaultSpec),
            persistCallback: gridSpec => XH.setPref(PREF_KEY, {gridSpec}),
            baseColumn: field => (field === 'ticker' ? this.tickerColumn() : positionsBaseColumn(field)),
            treeColumn: 'ticker',
            summaryRecords: store => store.records.filter(r => !r.parentId),
            gridConfig: {
                store: {fields: positionsStoreFields, projectionOnly: true},
                treeMode: true,
                treeStyle: TreeStyle.NONE,
                emptyText: 'No positions in this portfolio',
                enableExport: true,
                exportOptions: {
                    columns: ['VISIBLE'],
                    filename: 'portfolio'
                },
                filterModel: true,
                colDefaults: {filterable: false},
                autosizeOptions: {mode: 'onDemand'},
                onRowDoubleClicked: () => {
                    if (this.selectedPosition) this.showTransactions();
                }
            }
        });
    }

    /**
     * Ticker cell - hover for the ticker panel, background colored by today's price change.
     * Deliberately not `rendererIsComplex`: that force-refreshes the cell on every tick, which remounts it and closes an
     * open popup. Class rules are re-applied on each update regardless, and the popup reads the latest record on open.
     */
    private tickerColumn(): ColumnSpec {
        return {
            ...tickerCol,
            filterable: true,
            renderer: (v, {record}) =>
                isTickerRow(record)
                    ? tickerPopup({
                          position: record.data as Position,
                          getPosition: () => (record.store.getById(record.id) ?? record).data as Position
                      })
                    : strong(v),
            cellClassRules: tickerChangeClassRules
        };
    }
}
